import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { DailySalesService } from '../../../services/daily-sales.service';
import { LogService } from '../../../services/log.service';

interface ServiceStats {
  serviceName: string;
  totalSales: number;
  totalRevenue: number;
  totalWaste: number;
  averageAccuracy: number;
  totalRuptures: number;
  lastSaleDate: string | null;
  salesCount: number;
}

interface SaleRecord {
  _id: string;
  saleDate: string;
  serviceName: string;
  totalRevenue: number;
  totalWaste: number;
  productCount: number; // Nombre de types de produits (pas la somme des quantités)
  totalSold: number;
  totalPrepared: number;
  ruptureCount: number;
  isValidated: boolean;
  isDraft: boolean;
  hasRuptures: boolean;
  hasWaste: boolean;
  accuracy: number; // Pourcentage de précision
}

@Component({
  selector: 'app-merchant-services',
  imports: [CommonModule, FormsModule],
  templateUrl: './merchant-services.component.html',
  styleUrls: ['./merchant-services.component.css'],
})
export class MerchantServicesComponent implements OnInit {
  private log = inject(LogService);
  private cdr = inject(ChangeDetectorRef);

  services: any[] = [];
  selectedService: any = null;
  serviceStats: ServiceStats | null = null;
  isLoading = false;
  errorMessage = '';

  // Historique des ventes
  salesHistory: SaleRecord[] = [];
  filteredSalesHistory: SaleRecord[] = [];
  isLoadingHistory = false;

  // Filtres
  filterService: string = '';
  filterDateFrom: string = '';
  filterDateTo: string = '';
  filterStatus: string = 'all'; // 'all', 'validated', 'pending'

  // Dates manquantes (jours sans saisie)
  missingDates: string[] = [];

  constructor(
    private authService: AuthService,
    private dailySalesService: DailySalesService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.initFilters();
    this.loadUserServices();
    this.loadSalesHistory();
  }

  /**
   * Initialiser les filtres par défaut
   */
  initFilters(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    this.filterDateTo = today.toISOString().split('T')[0];
    this.filterDateFrom = thirtyDaysAgo.toISOString().split('T')[0];
  }

  /**
   * Charger les services de l'utilisateur depuis son profil
   */
  loadUserServices(): void {
    const user = this.authService.getCurrentUser();
    if (user && user.services) {
      this.services = user.services;
      this.log.debug('📋 Services utilisateur:', this.services);
    } else {
      this.errorMessage = 'Aucun service configuré dans votre profil';
    }
  }

  /**
   * Charger l'historique des ventes
   */
  loadSalesHistory(): void {
    this.isLoadingHistory = true;

    const params: any = {
      merchantType: 'fixed',
      from: this.filterDateFrom,
      to: this.filterDateTo,
    };

    if (this.filterService) {
      params.serviceName = this.filterService; // Utiliser serviceName (pas serviceId)
    }

    if (this.filterStatus === 'validated') {
      params.isValidated = true;
    } else if (this.filterStatus === 'pending') {
      params.isDraft = true;
    }

    this.dailySalesService.list(params).subscribe({
      next: (response) => {
        this.salesHistory = (response.data || []).map((sale: any) => {
          const summary = sale.summary || {};
          const totalWaste = summary.totalWaste || 0;
          const ruptureCount = summary.ruptureCount || 0;
          const productCount =
            sale.products?.length || summary.productCount || 0;

          return {
            _id: sale._id,
            saleDate: sale.saleDate,
            serviceName: sale.serviceName,
            totalRevenue: summary.totalRevenue || 0,
            totalWaste: totalWaste,
            productCount: productCount,
            totalSold: summary.totalSold || 0,
            totalPrepared: summary.totalPrepared || 0,
            ruptureCount: ruptureCount,
            isValidated: sale.isValidated || false,
            isDraft: sale.isDraft || false,
            hasRuptures: ruptureCount > 0,
            hasWaste: totalWaste > 0,
            accuracy: summary.averageAccuracy || 0,
          };
        });

        this.filteredSalesHistory = [...this.salesHistory];
        this.calculateMissingDates();
        this.isLoadingHistory = false;

        this.log.debug(`📊 ${this.salesHistory.length} ventes chargées`);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('Erreur chargement historique:', err);
        this.isLoadingHistory = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Calculer les dates manquantes (sans saisie)
   */
  calculateMissingDates(): void {
    if (!this.filterDateFrom || !this.filterDateTo) return;

    const existingDates = new Set<string>();

    // Collecter toutes les combinaisons date/service existantes
    this.salesHistory.forEach((sale) => {
      const dateKey = new Date(sale.saleDate).toISOString().split('T')[0];
      existingDates.add(`${dateKey}|${sale.serviceName}`);
    });

    // Vérifier les dates manquantes
    this.missingDates = [];
    const start = new Date(this.filterDateFrom);
    const end = new Date(this.filterDateTo);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let current = new Date(start);
    while (current <= end && current < today) {
      const dateStr = current.toISOString().split('T')[0];

      // Vérifier si au moins un service a une saisie pour cette date
      let hasAnySale = false;
      for (const service of this.services) {
        const serviceName = service.serviceName || service.name || service;
        if (existingDates.has(`${dateStr}|${serviceName}`)) {
          hasAnySale = true;
          break;
        }
      }

      if (!hasAnySale) {
        this.missingDates.push(dateStr);
      }

      current.setDate(current.getDate() + 1);
    }

    this.log.debug(`⚠️ ${this.missingDates.length} dates sans saisie`);
  }

  /**
   * Appliquer les filtres
   */
  applyFilters(): void {
    this.loadSalesHistory();
  }

  /**
   * Réinitialiser les filtres
   */
  resetFilters(): void {
    this.filterService = '';
    this.filterStatus = 'all';
    this.initFilters();
    this.loadSalesHistory();
  }

  /**
   * Naviguer vers la saisie des ventes
   */
  goToSalesEntry(): void {
    this.router.navigate(['/sales/entry-fixed']);
  }

  /**
   * Naviguer vers la saisie pour une date spécifique
   */
  goToSalesEntryForDate(date: string): void {
    this.router.navigate(['/sales/entry-fixed'], {
      queryParams: { date: date },
    });
  }

  /**
   * Ouvrir le détail d'une vente - aller directement à l'étape produits
   */
  openSaleDetail(sale: SaleRecord): void {
    this.router.navigate(['/sales/entry-fixed'], {
      queryParams: {
        date: new Date(sale.saleDate).toISOString().split('T')[0],
        service: sale.serviceName,
        step: 'products', // Aller directement à la saisie produits
      },
    });
  }

  /**
   * Formater une date pour l'affichage
   */
  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  /**
   * Sélectionner un service et charger ses statistiques
   */
  selectService(service: any): void {
    this.selectedService = service;
    this.loadServiceStats(service.serviceName || service.name || service);
  }

  /**
   * Charger les statistiques d'un service
   */
  loadServiceStats(serviceName: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    // Récupérer les 30 derniers jours
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    this.dailySalesService
      .list({
        merchantType: 'fixed',
        from: startDate.toISOString().split('T')[0],
        to: endDate.toISOString().split('T')[0],
        serviceName: serviceName, // Utiliser serviceName (pas serviceId)
        isDraft: false,
      })
      .subscribe({
        next: (response) => {
          const sales = response.data || [];
          this.log.debug(`📊 ${sales.length} ventes trouvées pour ${serviceName}`);

          this.serviceStats = this.calculateStats(serviceName, sales);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('Erreur chargement stats:', err);
          this.errorMessage = 'Erreur lors du chargement des statistiques';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Calculer les statistiques agrégées
   */
  calculateStats(serviceName: string, sales: any[]): ServiceStats {
    let totalRevenue = 0;
    let totalWaste = 0;
    let totalRuptures = 0;
    let totalAccuracy = 0;
    let accuracyCount = 0;
    let lastSaleDate: string | null = null;

    sales.forEach((sale) => {
      totalRevenue += sale.summary?.totalRevenue || 0;
      totalWaste += sale.summary?.totalWaste || 0;
      totalRuptures += sale.summary?.ruptureCount || 0;

      if (sale.summary?.averageAccuracy) {
        totalAccuracy += sale.summary.averageAccuracy;
        accuracyCount++;
      }

      const saleDate = new Date(sale.saleDate);
      if (!lastSaleDate || saleDate > new Date(lastSaleDate)) {
        lastSaleDate = sale.saleDate;
      }
    });

    return {
      serviceName,
      totalSales: sales.length,
      totalRevenue,
      totalWaste,
      averageAccuracy:
        accuracyCount > 0 ? Math.round(totalAccuracy / accuracyCount) : 0,
      totalRuptures,
      lastSaleDate,
      salesCount: sales.length,
    };
  }

  /**
   * Fermer le panneau de stats
   */
  closeStats(): void {
    this.selectedService = null;
    this.serviceStats = null;
  }

  /**
   * Voir l'historique des ventes du service
   */
  viewServiceHistory(serviceName: string): void {
    // TODO: Naviguer vers une page d'historique détaillé
    this.router.navigate(['/sales/history'], {
      queryParams: { service: serviceName },
    });
  }
}
